import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { getMotherDashboard, deleteMilkLog } from '../lib/api';
import { formatKL, formatKLShort } from '../lib/timezone';
import RiskBadge from '../components/RiskBadge';
import { toast } from 'sonner';
import {
  User, ClipboardCheck, Activity, Droplet, Plus, ArrowRight, LogOut, MessageCircle, Calendar, Eye, Trash2,
} from 'lucide-react';

const STORAGE_KEY = 'app_selfcheck';

export default function MyProfile() {
  const { lang, t } = useLanguage();
  const { mother, loading, logout } = useMother();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await getMotherDashboard();
      setData(res);
    } catch {
      toast.error(lang === 'zh' ? '加载失败' : 'Failed to load');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && !mother) {
      navigate('/profile/login?next=/my', { replace: true });
      return;
    }
    if (mother) refresh();
    // eslint-disable-next-line
  }, [mother, loading]);

  if (loading || !mother) {
    return <div className="container-app py-10 text-ink-tertiary">{t('loading')}</div>;
  }
  if (busy && !data) {
    return <div className="container-app py-10 text-ink-tertiary">{t('loading')}</div>;
  }

  const subs = data?.submissions || [];
  const logs = data?.milk_logs || [];

  // Today total (KL date)
  const todayKL = new Date().toLocaleDateString('en-GB', { timeZone: 'Asia/Kuala_Lumpur' });
  const todayLogs = logs.filter((l) => {
    const d = new Date(l.date_time);
    return d.toLocaleDateString('en-GB', { timeZone: 'Asia/Kuala_Lumpur' }) === todayKL;
  });
  const todayTotal = todayLogs.reduce((sum, l) => sum + (l.left_ml || 0) + (l.right_ml || 0), 0);

  const latestRisk = subs[0]?.risk_level || null;

  const viewResult = (sub) => {
    // Re-hydrate localStorage and route to /result so the existing renderer works.
    const payload = {
      ...sub,
      submission_id: sub.id,
      module_id: sub.result_type,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    navigate('/result');
  };

  const removeLog = async (id) => {
    try {
      await deleteMilkLog(id);
      toast.success(lang === 'zh' ? '已删除' : 'Deleted');
      refresh();
    } catch {
      toast.error(lang === 'zh' ? '删除失败' : 'Delete failed');
    }
  };

  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="my-profile-page">
      {/* Header */}
      <div className="card-base p-6 sm:p-8 mb-6 flex items-center gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-terracotta-light flex items-center justify-center flex-shrink-0">
          <User size={22} className="text-terracotta" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink" data-testid="my-mother-name">
            {mother.name}
          </h1>
          <div className="text-sm text-ink-secondary">
            {mother.phone} · {lang === 'zh' ? '宝宝生日' : 'Baby DOB'}: {mother.baby_birth_date}
          </div>
        </div>
        <button
          onClick={async () => { await logout(); navigate('/', { replace: true }); }}
          data-testid="my-logout-btn"
          className="btn-outline text-sm"
        >
          <LogOut size={14} className="mr-1" /> {t('profile_logout')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="card-base p-5" data-testid="stat-total-selfchecks">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-tertiary mb-2">
            <ClipboardCheck size={14} /> {t('profile_total_selfchecks')}
          </div>
          <div className="font-heading text-3xl font-semibold text-ink">{subs.length}</div>
        </div>
        <div className="card-base p-5" data-testid="stat-latest-risk">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-tertiary mb-2">
            <Activity size={14} /> {t('profile_latest_risk')}
          </div>
          {latestRisk ? <RiskBadge level={latestRisk} /> : <span className="text-ink-tertiary text-sm">—</span>}
        </div>
        <div className="card-base p-5" data-testid="stat-today-total">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-tertiary mb-2">
            <Droplet size={14} /> {t('profile_today_total')}
          </div>
          <div className="font-heading text-3xl font-semibold text-ink">
            {todayTotal} <span className="text-base font-normal text-ink-tertiary">ml</span>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-wrap gap-2 mb-8">
        <Link to="/self-check" data-testid="my-cta-start-check" className="btn-primary text-sm">
          <Plus size={14} className="mr-1" /> {t('profile_start_new_check')}
        </Link>
        <Link to="/milk-log" data-testid="my-cta-add-log" className="btn-secondary text-sm">
          <Droplet size={14} className="mr-1" /> {t('profile_add_log')}
        </Link>
        <Link to="/booking" data-testid="my-cta-book" className="btn-outline text-sm">
          <Calendar size={14} className="mr-1" /> {t('landing_btn_book')}
        </Link>
        {subs[0] && (
          <button
            onClick={() => { viewResult(subs[0]); setTimeout(() => navigate('/whatsapp-summary'), 50); }}
            data-testid="my-cta-whatsapp"
            className="btn-outline text-sm"
          >
            <MessageCircle size={14} className="mr-1" /> {t('profile_send_summary')}
          </button>
        )}
      </div>

      {/* Self-check history */}
      <section className="mb-10" data-testid="my-selfcheck-history">
        <h2 className="font-heading text-lg font-semibold text-ink mb-3">
          {lang === 'zh' ? 'Self-check 历史' : 'Self-check History'}
        </h2>
        {subs.length === 0 ? (
          <div className="card-base p-8 text-center text-ink-tertiary text-sm">
            {t('profile_no_selfchecks')}
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div
                key={s.id}
                data-testid={`selfcheck-row-${s.id}`}
                className="card-base p-4 sm:p-5 flex items-center gap-4 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <RiskBadge level={s.risk_level || 'green'} />
                    <span className="text-xs text-ink-tertiary">{formatKL(s.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium text-ink">
                    {s.main_concern || (lang === 'zh' ? '未指定' : 'Not specified')}
                  </div>
                  {s.result_type && (
                    <div className="text-[11px] text-ink-tertiary">Module {s.result_type}</div>
                  )}
                </div>
                <button
                  onClick={() => viewResult(s)}
                  data-testid={`view-result-${s.id}`}
                  className="btn-outline text-sm"
                >
                  <Eye size={14} className="mr-1" /> {t('profile_view_result')}
                  <ArrowRight size={12} className="ml-1" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent milk logs */}
      <section data-testid="my-milk-history">
        <h2 className="font-heading text-lg font-semibold text-ink mb-3">{t('profile_recent_logs')}</h2>
        {logs.length === 0 ? (
          <div className="card-base p-8 text-center text-ink-tertiary text-sm">
            {lang === 'zh' ? '还没有奶量记录' : 'No milk logs yet'}
          </div>
        ) : (
          <div className="space-y-2">
            {logs.slice(0, 10).map((l) => (
              <div
                key={l.id}
                data-testid={`milk-row-${l.id}`}
                className="card-base p-3 sm:p-4 flex items-center gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-ink-tertiary">{formatKLShort(l.date_time)} · {l.method}</div>
                  <div className="text-sm text-ink">
                    L {l.left_ml || 0} · R {l.right_ml || 0} ml
                    {l.duration ? ` · ${l.duration} min` : ''}
                    {l.has_lump ? ' · ⚠ lump' : ''}
                    {l.has_fever ? ' · ⚠ fever' : ''}
                  </div>
                </div>
                <button
                  onClick={() => removeLog(l.id)}
                  data-testid={`delete-milk-${l.id}`}
                  className="text-ink-tertiary hover:text-terracotta p-2 rounded-full hover:bg-subtle"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
